import { Scale } from "lucide-react";
import { Link } from "react-router-dom";
import { DocShell, DocCard } from "../components/DocShell";

export default function Terms() {
  return (
    <DocShell
      pageTestId="terms-page"
      icon={Scale}
      eyebrow="The fine print, kindly written"
      title="Terms of Service"
      subtitle="The agreement between you and Haplap when you use our community."
      updated="June 2026">
      <DocCard testId="terms-intro">
        <p>
          Welcome to Haplap. These Terms of Service ("Terms") are an agreement between you and Haplap
          ("Haplap", "we", "us"), a community platform based in Newfoundland, Canada. By creating an
          account or using Haplap, you agree to these Terms. If you do not agree, please do not use
          the platform.
        </p>
      </DocCard>

      <DocCard heading="1. Who can use Haplap" testId="terms-eligibility">
        <p>
          You must be of the age of majority in your region and able to enter a binding agreement.
          You are responsible for keeping your account credentials secure and for all activity under
          your account. Provide accurate information and keep it up to date.
        </p>
      </DocCard>

      <DocCard heading="2. Our community rules" testId="terms-conduct">
        <p>Haplap is a dignity-first, judgment-free space. We have <strong>zero tolerance</strong> for cruelty. You agree not to:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Harass, bully, threaten, demean or discriminate against anyone.</li>
          <li>Post hate speech, slurs, or content that shames people for asking for help.</li>
          <li>Impersonate others, lie about your identity, or misrepresent a need.</li>
          <li>Defraud the community, launder money, or solicit for illegal purposes.</li>
          <li>Spam, scrape, or attempt to break or abuse the platform.</li>
        </ul>
        <p>
          Violations may result in content removal, a 24-hour cool-down from a Community Guardian, or
          an immediate and permanent ban for severe abuse — including blocking re-registration.
        </p>
      </DocCard>

      <DocCard heading="3. Asking for help & your content" testId="terms-content">
        <p>
          You keep ownership of the content you post, but you grant Haplap a licence to display and
          distribute it on the platform so the community can see and support you. You are responsible
          for your content and confirm that any need or story you share is truthful. We may remove
          content or accounts that violate these Terms.
        </p>
      </DocCard>

      <DocCard heading="4. Giving, receiving & payments" testId="terms-payments">
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Donations, tips and jar contributions are gifts, not investments, loans or purchases, and they do not create any obligation from the recipient.</li>
          <li>Recipients keep 100% of direct tips; the donor covers the small platform fee and payment processing on top.</li>
          <li>The Community Tip Jar is shared evenly among people currently seeking help on a regular daily split.</li>
          <li>Payments are handled by our processor (Stripe). You agree to their terms when paying.</li>
          <li>All gifts are final and non-refundable — see our <Link to="/refund" className="text-copper underline">Refund Policy</Link>.</li>
        </ul>
      </DocCard>

      <DocCard heading="5. Wallets & cash-outs" testId="terms-wallet">
        <p>
          Support you receive is held in your Haplap wallet and can be withdrawn subject to
          verification and our processing. We may hold, review or reverse transactions where we
          reasonably suspect fraud, abuse, or a violation of these Terms.
        </p>
      </DocCard>

      <DocCard heading="6. Neighbors in Need groups" testId="terms-groups">
        <p>
          Group organizers are responsible for running their groups honestly and using pooled funds
          for their stated purpose. Haplap provides the tools but is not responsible for how
          individual groups or members choose to allocate their shared funds. Report any misuse to
          us.
        </p>
      </DocCard>

      <DocCard heading="7. Moderation & Guardians" testId="terms-moderation">
        <p>
          Community Guardians are trusted, admin-approved members who help keep Haplap kind. They and
          our admins may remove content and restrict accounts under these Terms. Moderation decisions
          are made to protect the community; we aim to be fair but reserve the right to act quickly
          on safety.
        </p>
      </DocCard>

      <DocCard heading="8. No guarantees" testId="terms-disclaimer">
        <p>
          Haplap connects people; we do not guarantee that any request will be funded, that any
          particular amount will be raised, or that every user's statements are accurate. The
          platform is provided "as is" and "as available" without warranties of any kind, to the
          fullest extent permitted by law.
        </p>
      </DocCard>

      <DocCard heading="9. Limitation of liability" testId="terms-liability">
        <p>
          To the fullest extent permitted by law, Haplap is not liable for indirect, incidental or
          consequential damages, or for the conduct of any user, recipient or third party. Our total
          liability for any claim is limited to the fees we earned on the transaction giving rise to
          the claim.
        </p>
      </DocCard>

      <DocCard heading="10. Termination" testId="terms-termination">
        <p>
          You may close your account at any time. We may suspend or terminate accounts that violate
          these Terms or put the community at risk. Some records are retained as described in our{" "}
          <Link to="/privacy" className="text-copper underline">Privacy Policy</Link>.
        </p>
      </DocCard>

      <DocCard heading="11. Governing law & contact" testId="terms-law">
        <p>
          These Terms are governed by the laws of the Province of Newfoundland and Labrador and the
          laws of Canada that apply there. We may update these Terms from time to time; continued use
          means you accept the changes. Questions? Email{" "}
          <a className="text-copper underline" href="mailto:legal@haplap.ca">legal@haplap.ca</a>.
        </p>
      </DocCard>
    </DocShell>
  );
}
